import type {
	OptimisticIssuePayload,
	CreateIssueParameters,
} from '@atlassian/jira-business-issue-create/src/types.tsx';
import { OPTIMISTIC_ID_PREFIX } from '@atlassian/jira-business-list-handlers/src/common/constants.tsx';
import {
	ASSIGNEE_TYPE,
	DATE_CF_TYPE,
	DATE_PICKER_GQL_FIELD,
	DUE_DATE_TYPE,
	ISSUE_KEY_TYPE,
	ISSUE_TYPE_GQL_FIELD,
	RANK_CF_TYPE,
	STATUS_GQL_FIELD,
	STATUS_TYPE,
	SUMMARY_TYPE,
	TEXT_GQL_FIELD,
	USER_PICKER_GQL_FIELD,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import type {
	JWMTimelineInlineCreateIssue_createJiraBusinessIssue_item as JWMTimelineViewItemsNode,
	JWMTimelineInlineCreateIssueVariables,
} from './__generated_apollo__/JWMTimelineInlineCreateIssue';

const RANK_BEFORE_ID = 'rankBefore';
const RANK_AFTER_ID = 'rankAfter';
type CreateOptimisticIssue = (options: OptimisticIssuePayload) => JWMTimelineViewItemsNode;

export const createOptimisticIssue: CreateOptimisticIssue = ({
	issueId,
	summary,
	issueType,
	rank,
	status,
}) => ({
	id: `${OPTIMISTIC_ID_PREFIX}${issueId}`,
	issueId,
	__typename: 'JiraWorkManagementViewItem',
	childSummary: {
		__typename: 'JiraWorkManagementChildSummary',
		hasChildren: false,
	},
	issueLinks: {
		edges: [],
		__typename: 'JiraIssueLinkConnection',
	},
	fields: [
		{
			aliasFieldId: null,
			fieldId: 'summary',
			id: `${OPTIMISTIC_ID_PREFIX}${issueId}${SUMMARY_TYPE}`,
			text: summary ?? null,
			type: SUMMARY_TYPE,
			__typename: TEXT_GQL_FIELD,
		},
		{
			fieldId: 'issuekey',
			aliasFieldId: null,
			id: `${OPTIMISTIC_ID_PREFIX}${issueId}${ISSUE_KEY_TYPE}`,
			type: ISSUE_KEY_TYPE,
			text: '',
			__typename: TEXT_GQL_FIELD,
		},
		{
			fieldId: 'issuetype',
			aliasFieldId: null,
			id: `${OPTIMISTIC_ID_PREFIX}${issueId}issuetype`,
			type: 'issuetype',
			issueType: {
				__typename: 'JiraIssueType',
				issueTypeId: issueType.id,
				name: issueType.name,
				hierarchy: {
					level: issueType.hierarchyLevel,
					__typename: 'JiraIssueTypeHierarchyLevel',
				},
				avatar: {
					__typename: 'JiraAvatar',
					medium: issueType.avatarUrl,
				},
			},
			__typename: ISSUE_TYPE_GQL_FIELD,
		},
		{
			fieldId: 'customfield_10019',
			id: `${OPTIMISTIC_ID_PREFIX}${issueId}${RANK_CF_TYPE}`,
			aliasFieldId: 'rank',
			type: RANK_CF_TYPE,
			text: rank ?? 'z',
			__typename: TEXT_GQL_FIELD,
		},
		{
			fieldId: 'status',
			aliasFieldId: null,
			id: `${OPTIMISTIC_ID_PREFIX}${issueId}${STATUS_TYPE}`,
			type: STATUS_TYPE,
			status: {
				name: status?.name ?? 'To Do',
				statusId: status?.statusId ?? '0',
				statusCategory: {
					statusCategoryId: String(status?.statusCategoryId ?? 2),
					__typename: 'JiraStatusCategory',
				},
				__typename: 'JiraStatus',
			},
			__typename: STATUS_GQL_FIELD,
		},
		{
			fieldId: 'duedate',
			aliasFieldId: null,
			id: `${OPTIMISTIC_ID_PREFIX}${issueId}${DUE_DATE_TYPE}`,
			type: DUE_DATE_TYPE,
			date: null,
			__typename: DATE_PICKER_GQL_FIELD,
		},
		{
			fieldId: 'assignee',
			aliasFieldId: null,
			id: `${OPTIMISTIC_ID_PREFIX}${issueId}${ASSIGNEE_TYPE}`,
			type: ASSIGNEE_TYPE,
			user: null,
			__typename: USER_PICKER_GQL_FIELD,
		},
		{
			fieldId: 'customfield_10015',
			aliasFieldId: 'startdate',
			id: `${OPTIMISTIC_ID_PREFIX}${issueId}${DATE_CF_TYPE}`,
			type: DATE_CF_TYPE,
			date: null,
			__typename: DATE_PICKER_GQL_FIELD,
		},
	],
});

export const buildInput = (
	input: CreateIssueParameters['input'],
): JWMTimelineInlineCreateIssueVariables['input'] => {
	const singleLineTextFields = [
		{
			fieldId: SUMMARY_TYPE,
			text: input.summary,
		},
	];

	const fields = {
		singleLineTextFields,
		...(input.parentIssueId && {
			parentField: {
				issueId: input.parentIssueId,
			},
		}),
	};

	const rank = {
		...(input.rankBefore && {
			before: {
				id: RANK_BEFORE_ID,
				issueId: input.rankBefore.issueId,
			},
		}),
		...(input.rankAfter && {
			after: {
				id: RANK_AFTER_ID,
				issueId: input.rankAfter.issueId,
			},
		}),
	};

	const result = {
		projectId: input.projectId,
		issueType: input.issueType.id,
		fields,
		rank,
	};

	return result;
};
